import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { maintenanceApi, vehiclesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Wrench, Loader2 } from 'lucide-react';

const MAINTENANCE_TYPES = ['preventive', 'repair', 'emergency'];
const SERVICE_TYPES = ['Oil Change', 'Brake Service', 'Tyre Replacement', 'AC Service', 'Battery Change', 'Engine Tune-up', 'General Service', 'Suspension Work', 'Electrical Repair'];

const initialFormState = {
  vehicle_id: '',
  maintenance_type: '',
  service_type: '',
  vendor_name: '',
  scheduled_date: '',
  completed_date: '',
  cost: '',
  parts_replaced: '',
  next_service_km: '',
  next_service_date: '',
  status: 'scheduled',
  notes: '',
};

const Maintenance = () => {
  const [maintenance, setMaintenance] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [maintenanceRes, vehiclesRes] = await Promise.all([
        maintenanceApi.getAll(),
        vehiclesApi.getAll(),
      ]);
      setMaintenance(maintenanceRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        cost: parseFloat(formData.cost),
        next_service_km: formData.next_service_km ? parseInt(formData.next_service_km) : null,
      };

      await maintenanceApi.create(data);
      toast.success('Maintenance record added');

      setDialogOpen(false);
      setFormData(initialFormState);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
      in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    return (
      <Badge className={`${variants[status]} capitalize`}>
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getVehicleName = (id) => {
    const v = vehicles.find(v => v.id === id);
    return v ? v.registration_number : 'N/A';
  };

  return (
    <Layout
      title="Maintenance"
      subtitle="Workshop & Service Management"
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setFormData(initialFormState);
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="add-maintenance-btn">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                Schedule Maintenance
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle *</Label>
                  <Select
                    value={formData.vehicle_id}
                    onValueChange={(v) => handleSelectChange('vehicle_id', v)}
                  >
                    <SelectTrigger data-testid="maintenance-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.registration_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Maintenance Type *</Label>
                  <Select
                    value={formData.maintenance_type}
                    onValueChange={(v) => handleSelectChange('maintenance_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Service Type *</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(v) => handleSelectChange('service_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_name">Vendor/Workshop *</Label>
                  <Input
                    id="vendor_name"
                    name="vendor_name"
                    placeholder="e.g., ABC Garage"
                    value={formData.vendor_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scheduled_date">Scheduled Date *</Label>
                  <Input
                    id="scheduled_date"
                    name="scheduled_date"
                    type="date"
                    value={formData.scheduled_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cost">Estimated Cost (₹) *</Label>
                  <Input
                    id="cost"
                    name="cost"
                    type="number"
                    min="0"
                    placeholder="e.g., 5000"
                    value={formData.cost}
                    onChange={handleInputChange}
                    required
                    data-testid="maintenance-cost-input"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    name="notes"
                    placeholder="Additional notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="maintenance-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Schedule Service'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6" data-testid="maintenance-page">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Vehicle</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Scheduled Date</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : maintenance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Wrench className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No maintenance records found</p>
                  </TableCell>
                </TableRow>
              ) : (
                maintenance.map((item) => (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{getVehicleName(item.vehicle_id)}</TableCell>
                    <TableCell>{item.service_type}</TableCell>
                    <TableCell>{item.vendor_name}</TableCell>
                    <TableCell>{item.scheduled_date}</TableCell>
                    <TableCell className="tabular-nums">{formatCurrency(item.cost)}</TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
};

export default Maintenance;
